import { isValidPaymentSelection } from '../types/schedule.types';
import type { QuoteStep, QuoteSummary } from '../types/quote.types';
import { minimumConfiguredServices } from '@features/service-plans/utils/minimumConfiguredServices';
import { isVehicleMissingRegistrationDate } from './vehicleDocument';
import { isLiveQuote } from './quoteStatus';

type RestoredDealer = QuoteSummary['dealer'] | null;

const isLoadedStepValid: Record<QuoteStep, (q: QuoteSummary, restoredDealer: RestoredDealer) => boolean> = {
    dealer: (_q, restoredDealer) => !!restoredDealer,
    vehicle: (q) => !!q.vehicle,
    product: (q) => !!q.product,
    configuration: (q) =>
        Array.isArray(q.services) && q.services.length >= minimumConfiguredServices(q.product),
    customer: (q) => !!q.customer,
    payment: (q) => isValidPaymentSelection(q.paymentSelection) || !!(q.payment && q.payment.startDate),
    summary: () => true,
};

interface ResumeInput {
    order: readonly QuoteStep[];
    restoredDealer: RestoredDealer;
    overridesDiscarded: boolean;
}

export type QuoteLanding = { kind: 'resume' } | { kind: 'keep'; step: QuoteStep };

/** The first step the loaded quote cannot satisfy, or `summary` when every step is satisfied. */
export const resumeStepFor = (
    quote: QuoteSummary,
    { order, restoredDealer, overridesDiscarded }: ResumeInput,
): QuoteStep => {
    if (isVehicleMissingRegistrationDate(quote.vehicle)) return 'vehicle';
    const isStepValid = (step: QuoteStep): boolean =>
        overridesDiscarded && step === 'payment' ? false : isLoadedStepValid[step](quote, restoredDealer);
    return order.find((step) => !isStepValid(step)) ?? 'summary';
};

/** `keep` holds the current step unless the server copy is not live or the kept step sits past the resume step. */
export const landingStep = (
    order: readonly QuoteStep[],
    landing: QuoteLanding,
    resume: QuoteStep,
    status: QuoteSummary['status'],
): QuoteStep => {
    if (landing.kind === 'resume') return resume;
    if (!isLiveQuote(status)) return resume;
    const keptIndex = order.indexOf(landing.step);
    return keptIndex === -1 || keptIndex > order.indexOf(resume) ? resume : landing.step;
};
